import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const API_BASE = (process.env.REACT_APP_API_BASE || '/api').replace(/["']/g, '').replace(/\/+$/, '');

// ✅ Word Mapping Table Colors (Removed "Level" suffix)
const COMPETENCY_COLORS = {
  Foundational: '#6c757d',
  Emerging: '#dc3545',
  Developing: '#ffc107',
  Proficient: '#007BFF',
  Advanced: '#28a745'
};

// ✅ Helper: Get competency word from score (Removed "Level" suffix)
const getCompetencyWord = (score) => {
  if (score >= 86) return 'Advanced';
  if (score >= 71) return 'Proficient';
  if (score >= 51) return 'Developing';
  if (score >= 31) return 'Emerging';
  return 'Foundational';
};

// ✅ Translation Dictionary
const translations = {
  en: {
    dashboardTitle: 'Parent Dashboard',
    welcome: 'Welcome',
    logout: 'Logout',
    selectStudent: 'Select Student',
    student: 'Student',
    centerId: 'Center ID',
    grade: 'Grade',
    school: 'School',
    subjectProgress: 'Subject Progress',
    english: 'English',
    math: 'Math',
    science: 'Science',
    notAssessed: 'Not Assessed',
    recentDiagnostic: 'Most Recent Diagnostic',
    competencyLevel: 'Competency',
    noTestTaken: 'No diagnostic test taken yet',
    viewResults: 'View Results',
    loading: 'Loading dashboard...',
    
    // Score Bar Levels (No "Level" word)
    Foundational: 'Foundational',
    Emerging: 'Emerging',
    Developing: 'Developing',
    Proficient: 'Proficient',
    Advanced: 'Advanced',

    // English Skill Analysis
    'Recognising Similar Meaning': 'Recognising Similar Meaning',
    'Understanding Question Requirements': 'Understanding Question Requirements',
    'Understanding Sentence Structure': 'Understanding Sentence Structure',
    'Making Logical Conclusions': 'Making Logical Conclusions',
    'Understanding Word Meaning': 'Understanding Word Meaning',
    'Grammar Accuracy': 'Grammar Accuracy',
    'Finding Key Information': 'Finding Key Information',
    'Connecting Ideas Across Text': 'Connecting Ideas Across Text',
    'Understanding Deeper Meaning': 'Understanding Deeper Meaning',

    // Math Misconceptions
    'Concept Misunderstanding': 'Concept Misunderstanding',
    'Step Sequencing': 'Step Sequencing',
    'Calculation Error': 'Calculation Error',
    'Instruction Misinterpretation': 'Instruction Misinterpretation',
    'Vocabulary Misunderstanding': 'Vocabulary Misunderstanding',

    // Math Skill Analysis
    'Fundamental Concept': 'Fundamental Concept',
    'Multi-Step Problem Solving': 'Multi-Step Problem Solving',
    'Word Problem': 'Word Problem',
    'Math Representation': 'Math Representation',
    'Logical Reasoning': 'Logical Reasoning'
  },
  cn: {
    dashboardTitle: '家长控制台',
    welcome: '欢迎',
    logout: '退出登录',
    selectStudent: '选择学生',
    student: '学生',
    centerId: '中心 ID',
    grade: '年级',
    school: '学校',
    subjectProgress: '学科进度',
    english: '英语',
    math: '数学',
    science: '科学',
    notAssessed: '未评估',
    recentDiagnostic: '最近一次诊断测试',
    competencyLevel: '能力等级',
    noTestTaken: '尚未参加诊断测试',
    viewResults: '查看结果',
    loading: '正在加载控制台...',
    
    // Score Bar Levels (No "级" / "Level" word)
    Foundational: '基础',
    Emerging: '初级',
    Developing: '发展',
    Proficient: '熟练',
    Advanced: '高级',

    // English Skill Analysis
    'Recognising Similar Meaning': '语义辨析能力',
    'Understanding Question Requirements': '题目理解能力',
    'Understanding Sentence Structure': '语法分析能力',
    'Making Logical Conclusions': '逻辑推理能力',
    'Understanding Word Meaning': '词义理解能力',
    'Grammar Accuracy': '语法运用能力',
    'Finding Key Information': '关键信息提取能力',
    'Connecting Ideas Across Text': '文本信息整合能力',
    'Understanding Deeper Meaning': '深层语义理解能力',

    // Math Misconceptions
    'Concept Misunderstanding': '概念理解偏差',
    'Step Sequencing': '解题步骤组织偏差',
    'Calculation Error': '运算准确性偏差',
    'Instruction Misinterpretation': '题目指令理解偏差',
    'Vocabulary Misunderstanding': '数学术语理解偏差',

    // Math Skill Analysis
    'Fundamental Concept': '基础概念掌握',
    'Multi-Step Problem Solving': '多步骤问题解决能力',
    'Word Problem': '文字题理解与应用能力',
    'Math Representation': '数学表征能力',
    'Logical Reasoning': '数学逻辑推理能力'
  }
};

export default function ParentDashboard() {
  const navigate = useNavigate();
  const [lang, setLang] = useState('en'); 
  const [parent, setParent] = useState(null);
  const [students, setStudents] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const t = (key) => translations[lang][key] || key;

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day} ${month} ${year}`; 
  };

  useEffect(() => { loadDashboard(); }, []);

  const loadDashboard = async () => {
    try {
      const token = localStorage.getItem('token');
      const userRaw = localStorage.getItem('user');
      
      if (!token || !userRaw) {
        navigate('/login');
        return;
      }

      const user = JSON.parse(userRaw);
      if (user.role !== 'parent') {
        navigate('/login');
        return;
      }
      
      setParent(user);
      
      const res = await fetch(`${API_BASE}/parent/dashboard`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!res.ok) throw new Error('Failed to fetch dashboard');
      const result = await res.json();
      
      setStudents(result.students);
      if (result.students.length > 0) setSelectedStudent(result.students[0]);
    } catch (err) {
      console.error('Dashboard load error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center' }}>⏳ {t('loading')}</div>;
  if (error) return <div style={{ padding: 20, background: '#f8d7da', color: '#721c24', margin: 20, borderRadius: 6 }}>❌ {error}</div>;

  const subjectList = [
    { key: 'English', label: t('english'), icon: '📖' },
    { key: 'Math', label: t('math'), icon: '🔢' },
    { key: 'Science', label: t('science'), icon: '🔬' }
  ];

  return (
    <div style={{ maxWidth: 1200, margin: '40px auto', padding: 24, background: '#f8f9fa', minHeight: '100vh' }}>
      
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 30, flexWrap: 'wrap', gap: 15 }}>
        <div>
          <h2 style={{ margin: 0 }}>{t('dashboardTitle')}</h2>
          <p style={{ color: '#666', margin: '5px 0 0 0' }}>{t('welcome')}, {parent?.first_name} {parent?.last_name}</p>
        </div>
        
        <div style={{ display: 'flex', alignItems: 'center', gap: 15 }}>
          <div style={{ display: 'flex', border: '1px solid #ced4da', borderRadius: 6, overflow: 'hidden' }}>
            <button 
              onClick={() => setLang('en')} 
              style={{ 
                background: lang === 'en' ? '#007BFF' : '#fff', 
                color: lang === 'en' ? '#fff' : '#333', 
                border: 'none', 
                padding: '8px 14px', 
                cursor: 'pointer', 
                fontWeight: 700,
                fontSize: 13,
                transition: 'all 0.2s ease'
              }}
            >
              EN
            </button>
            <button 
              onClick={() => setLang('cn')} 
              style={{ 
                background: lang === 'cn' ? '#007BFF' : '#fff', 
                color: lang === 'cn' ? '#fff' : '#333', 
                border: 'none', 
                borderLeft: '1px solid #ced4da', 
                padding: '8px 14px', 
                cursor: 'pointer', 
                fontWeight: 700,
                fontSize: 13,
                transition: 'all 0.2s ease'
              }}
            >
              CN
            </button>
          </div>

          <button onClick={handleLogout} style={{ padding: '10px 16px', background: '#dc3545', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}>
            {t('logout')}
          </button>
        </div>
      </div>

      {students.length > 1 && (
        <div style={{ background: '#fff', padding: 20, borderRadius: 8, border: '1px solid #ddd', marginBottom: 20 }}>
          <h3 style={{ marginTop: 0, marginBottom: 15, fontSize: 16 }}>{t('selectStudent')}</h3>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {students.map(student => (
              <button 
                key={student.id} 
                onClick={() => setSelectedStudent(student)} 
                style={{ 
                  padding: '10px 20px', 
                  background: selectedStudent?.id === student.id ? '#007BFF' : '#e9ecef', 
                  color: selectedStudent?.id === student.id ? '#fff' : '#333', 
                  border: 'none', 
                  borderRadius: 6, 
                  cursor: 'pointer', 
                  fontWeight: selectedStudent?.id === student.id ? 600 : 400,
                  transition: 'all 0.2s ease'
                }}
              >
                {student.first_name} {student.last_name}
              </button>
            ))}
          </div>
        </div>
      )}

      {selectedStudent && (
        <>
          <div style={{ background: '#fff', padding: 20, borderRadius: 8, border: '1px solid #ddd', marginBottom: 20 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 15 }}>
              <div><strong>{t('student')}:</strong> {selectedStudent.first_name} {selectedStudent.last_name}</div>
              <div><strong>{t('centerId')}:</strong> {selectedStudent.student_id || 'N/A'}</div>
              <div><strong>{t('grade')}:</strong> {selectedStudent.current_grade || 'N/A'}</div>
              <div><strong>{t('school')}:</strong> {selectedStudent.school_name || 'N/A'}</div>
            </div>
          </div>

          <h3 style={{ marginBottom: 15 }}>{t('subjectProgress')}</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: 20 }}>
            {subjectList.map(subject => {
              const data = selectedStudent.subjects[subject.key];
              
              // ✅ Get competency word key and translate it
              const score = data?.score || 0;
              const competencyWordKey = getCompetencyWord(score);
              const competencyWord = t(competencyWordKey); // Translates to "高级" or "Advanced"
              const competencyColor = COMPETENCY_COLORS[competencyWordKey] || '#ccc';
              
              return (
                <div key={subject.key} style={{ 
                  background: '#fff', 
                  padding: 25, 
                  borderRadius: 8, 
                  border: '1px solid #ddd',
                  borderTop: `5px solid ${data ? competencyColor : '#ccc'}`
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                    <h4 style={{ margin: 0, fontSize: 18 }}>{subject.icon} {subject.label}</h4>
                    {data && (
                      <span style={{ 
                        padding: '6px 16px', 
                        background: competencyColor, 
                        color: '#fff', 
                        borderRadius: 12, 
                        fontSize: 14, 
                        fontWeight: 600 
                      }}>
                        {competencyWord}
                      </span>
                    )}
                  </div>
                  
                  {data ? (
                    <>
                      <div style={{ marginBottom: 15 }}>
                        <div style={{ fontSize: 13, color: '#666', marginBottom: 5 }}>{t('recentDiagnostic')}</div>
                        <div style={{ fontSize: 15, fontWeight: 600 }}>{data.exam_name}</div>
                        <div style={{ fontSize: 13, color: '#666', marginTop: 5 }}>
                          {t('competencyLevel')}: <strong style={{ color: competencyColor }}>{competencyWord}</strong> · {formatDate(data.completed_at)}
                        </div>
                      </div>
                      <button 
                        onClick={() => navigate(`/parent/results/${data.assignment_id}`)} 
                        style={{ width: '100%', padding: '12px', background: '#007BFF', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}
                      >
                         {t('viewResults')}
                      </button>
                    </>
                  ) : (
                    <div style={{ padding: 20, textAlign: 'center', color: '#666', background: '#f8f9fa', borderRadius: 6 }}>
                      ⚠️ {t('noTestTaken')}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}